import React, { useState, useEffect, useRef } from 'react';
import VirtualKeyboard from './VirtualKeyboard';
import metricsTracker from '../utils/metricsTracker';

const Exam = ({ updateMetrics }) => {
  // Exam state
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionsPerPage, setQuestionsPerPage] = useState(20);
  const [examStarted, setExamStarted] = useState(false);
  const [examCompleted, setExamCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [examProgress, setExamProgress] = useState({});
  const [examConfig, setExamConfig] = useState({
    questionCount: 2,
    gradeLevel: '1',
    operation: 'addition',
    useTimer: false,
    timeLimit: 30 // minutes
  });
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  // Virtual keyboard state
  const [keyboardVisible, setKeyboardVisible] = useState(true);
  const [keyboardPosition, setKeyboardPosition] = useState({ x: 400, y: 100 }); // Initial position to the right
  const [activeInput, setActiveInput] = useState(null);
  const mathProblemRefs = useRef({});

  // Level difficulty ranges - properly aligned with "Upto X digits"
  const gradeRanges = {
    '1': { min: 1, max: 99 },        // Upto 2 digits
    '2': { min: 10, max: 999 },      // Upto 3 digits
    '3': { min: 100, max: 9999 },    // Upto 4 digits
    '4': { min: 1000, max: 99999 },  // Upto 5 digits
    '5': { min: 10000, max: 999999 }, // Upto 6 digits
    '6': { min: 100000, max: 9999999 }, // Upto 7 digits
    '7': { min: 1000000, max: 99999999 }, // Upto 8 digits
    '8': { min: 10000000, max: 999999999 } // Upto 9 digits
  };

  // Calculate paper savings
  const calculatePaperSavings = () => {
    const linesPerQuestion = 3; // Each question takes about 3 lines (problem + working + answer)
    const validTotalQuestions = Number.isFinite(totalQuestions) ? totalQuestions : 0;
    const totalLines = validTotalQuestions * linesPerQuestion;
    const papersSaved = Math.ceil(totalLines / 25); // 25 lines per A4 sheet
    return { papersSaved, totalLines };
  };

  // Timer functions
  const formatTime = (seconds) => {
    // Handle invalid input
    if (!seconds || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    const totalSeconds = examConfig.timeLimit * 60;
    setTimeRemaining(totalSeconds);
    setTimerActive(true);
  };

  const stopTimer = () => {
    setTimerActive(false);
  };

  // Generate exam questions
  const generateExam = () => {
    const questions = [];
    const range = gradeRanges[examConfig.gradeLevel];
    
    for (let i = 0; i < examConfig.questionCount; i++) {
      const num1 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      const num2 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      const correctAnswer = num1 + num2;
      
      questions.push({
        id: i + 1,
        num1: num1.toString(),
        num2: num2.toString(),
        correctAnswer: correctAnswer.toString(),
        userAnswer: '',
        carryOver: '',
        isCorrect: null,
        explanation: generateExplanation(num1, num2, correctAnswer)
      });
    }
    
    setExamQuestions(questions);
    setTotalQuestions(examConfig.questionCount);
    setCurrentPage(1);
    setExamStarted(true);
    setExamCompleted(false);
    setShowResults(false);
    setExamProgress({});
    
    // Track exam started
    metricsTracker.trackExamStarted(examConfig.operation, examConfig.gradeLevel, examConfig.questionCount);
    
    // Start timer if enabled
    if (examConfig.useTimer) {
      startTimer();
    }
    
    // Save exam to localStorage
    saveExamToStorage(questions);
    
    // Report metrics for new exam
    if (updateMetrics) {
      const { papersSaved } = calculatePaperSavings();
      updateMetrics(papersSaved);
    }
  };

  // Generate step-by-step explanation
  const generateExplanation = (num1, num2, result) => {
    const aDigits = num1.toString().split('').reverse();
    const bDigits = num2.toString().split('').reverse();
    const maxLength = Math.max(aDigits.length, bDigits.length);
    
    let steps = [];
    let carry = 0;
    
    for (let i = 0; i < maxLength; i++) {
      const digitA = parseInt(aDigits[i] || 0);
      const digitB = parseInt(bDigits[i] || 0);
      const sum = digitA + digitB + carry;
      const resultDigit = sum % 10;
      const newCarry = Math.floor(sum / 10);
      
      let placeValue = '';
      if (i === 0) placeValue = 'ones';
      else if (i === 1) placeValue = 'tens';
      else if (i === 2) placeValue = 'hundreds';
      else if (i === 3) placeValue = 'thousands';
      else if (i === 4) placeValue = 'ten thousands';
      else if (i === 5) placeValue = 'hundred thousands';
      else if (i === 6) placeValue = 'millions';
      else placeValue = `${Math.pow(10, i)}s`;
      
      steps.push({
        position: i + 1,
        digitA,
        digitB,
        carry: carry,
        sum,
        resultDigit,
        newCarry: newCarry,
        placeValue
      });
      
      carry = newCarry;
    }
    
    if (carry > 0) {
      steps.push({
        position: maxLength + 1,
        digitA: 0,
        digitB: 0,
        carry: carry,
        sum: carry,
        resultDigit: carry,
        newCarry: 0,
        placeValue: maxLength === 1 ? 'tens' : maxLength === 2 ? 'hundreds' : maxLength === 3 ? 'thousands' : `${Math.pow(10, maxLength)}s`
      });
    }
    
    return { steps, result };
  };

  // Handle answer input
  const handleAnswerChange = (questionId, index, value) => {
    // Validate input - only allow single digits 0-9
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const updatedQuestions = examQuestions.map(q => {
        if (q.id === questionId) {
          const newAnswer = [...(q.userAnswer || [])];
          newAnswer[index] = value;
          return { ...q, userAnswer: newAnswer };
        }
        return q;
      });
      setExamQuestions(updatedQuestions);
      
      // Auto-save progress
      saveExamToStorage(updatedQuestions);
    }
  };

  // Handle carry input
  const handleCarryChange = (questionId, index, value) => {
    // Validate input - only allow single digits 0-9
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const updatedQuestions = examQuestions.map(q => {
        if (q.id === questionId) {
          const newCarry = [...(q.carryOver || [])];
          newCarry[index] = value;
          return { ...q, carryOver: newCarry };
        }
        return q;
      });
      setExamQuestions(updatedQuestions);
      
      // Auto-save progress
      saveExamToStorage(updatedQuestions);
    }
  };

  // Submit exam
  const submitExam = () => {
    const updatedQuestions = examQuestions.map(q => ({
      ...q,
      isCorrect: (q.userAnswer || []).join('') === q.correctAnswer
    }));
    
    // Calculate score
    const correctAnswers = updatedQuestions.filter(q => q.isCorrect).length;
    const score = Math.round((correctAnswers / updatedQuestions.length) * 100);
    
    setExamQuestions(updatedQuestions);
    setExamCompleted(true);
    setShowResults(true);
    
    // Stop timer
    stopTimer();
    
    // Save results
    saveExamToStorage(updatedQuestions);
    
    // Track exam submitted
    metricsTracker.trackExamSubmitted(examConfig.operation, examConfig.gradeLevel, score, updatedQuestions.length, correctAnswers);
    
    // Scroll to top to show Exam Results Summary
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Save exam to localStorage
  const saveExamToStorage = (questions) => {
    const examData = {
      questions,
      config: examConfig,
      timestamp: new Date().toISOString(),
      completed: examCompleted
    };
    localStorage.setItem('bellMathsExam', JSON.stringify(examData));
  };

  // Load exam from localStorage
  const loadExamFromStorage = () => {
    const savedExam = localStorage.getItem('bellMathsExam');
    if (savedExam) {
      const examData = JSON.parse(savedExam);
      setExamQuestions(examData.questions || []);
      setExamConfig(examData.config || examConfig);
      setExamCompleted(examData.completed || false);
      setTotalQuestions(examData.questions?.length || 0);
      if (examData.completed) {
        setShowResults(true);
      }
    }
  };

  // Print exam
  const printExam = () => {
    // Track print workbook
    metricsTracker.trackPrintWorkbook(examConfig.operation, examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>BellMaths Exam</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header h1 {
              color: #667eea;
              margin: 0 0 10px 0;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .question { 
              margin-bottom: 40px; 
              page-break-inside: avoid; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .question-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }
            .question-header h3 {
              margin: 0;
              color: #667eea;
            }
            .math-problem {
              margin: 20px 0;
            }
            /* Grid layout for side-by-side question and solution */
            .question > div[style*="grid-template-columns"] {
              display: grid !important;
              grid-template-columns: 1fr 1fr !important;
              gap: 20px !important;
              align-items: start !important;
            }
            .problem-grid {
              display: grid;
              gap: 0px;
              align-items: center;
              margin-bottom: 20px;
            }
            .digit-input {
              width: 40px;
              height: 50px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f4fd;
            }
            .carry-input {
              width: 30px;
              height: 25px;
              font-size: 0.8rem;
              text-align: center;
              border: none;
              border-bottom: 2px solid #e67e22;
              border-radius: 0;
              background-color: #fff3e0;
              color: #e67e22;
              font-weight: bold;
            }
            .operator {
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              color: #333;
            }
            .answer-line {
              height: 2px;
              background-color: #333;
              margin: 10px 0;
            }
            .answer-input {
              width: 20px;
              height: 30px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: none;
              border-radius: 0;
              background-color: transparent;
              outline: none;
              position: relative;
            }
            .answer-input::after {
              content: '';
              position: absolute;
              bottom: 5px;
              left: 50%;
              transform: translateX(-50%);
              width: 25px;
              height: 3px;
              background-color: #333;
              border-radius: 1px;
            }
            .carry-label {
              font-size: 0.8rem;
              color: #e67e22;
              font-weight: bold;
            }
            .page-break { 
              page-break-before: always; 
            }
            .solution-section {
              margin-top: 20px;
              padding: 15px;
              background-color: #f8f9fa;
              border-radius: 6px;
              border: 1px solid #e9ecef;
            }
            .solution-section h4 {
              margin: 0 0 10px 0;
              color: #667eea;
            }
            .explanation-steps {
              font-size: 14px;
            }
            .explanation-steps > div {
              margin-bottom: 8px;
              padding: 6px;
              background-color: white;
              border-radius: 4px;
              border: 1px solid #e9ecef;
              font-size: 13px;
            }
            .explanation-steps strong {
              color: #667eea;
            }
            .final-answer {
              margin-top: 8px;
              padding: 6px;
              background-color: #d4edda;
              border-radius: 4px;
              border: 1px solid #c3e6cb;
              text-align: center;
              font-weight: bold;
              color: #155724;
              font-size: 13px;
            }
            @media print {
              body { background-color: white; }
              .question { box-shadow: none; border: 1px solid #ddd; }
              .header { box-shadow: none; border: 1px solid #ddd; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  // Generate print content
  const generatePrintContent = () => {
    // Debug: Check if questions have explanation data
    console.log('Exam questions for print:', examQuestions);
    examQuestions.forEach((q, i) => {
      console.log(`Question ${i + 1} explanation:`, q.explanation);
    });
    
    let content = `
      <div class="header">
        <h1>BellMaths - Addition Exam</h1>
        <p>Grade Level: ${examConfig.gradeLevel} | Total Questions: ${totalQuestions}</p>
        <p>Date: ${new Date().toLocaleDateString()}</p>
        <p>Student Name: _________________</p>
        <p style="color: #28a745; font-style: italic;">🌱 Eco-Friendly: This digital exam saves paper and helps protect our environment!</p>
      </div>
    `;

    examQuestions.forEach((question, index) => {
      if (index > 0 && index % 5 === 0) {
        content += '<div class="page-break"></div>';
      }
      
      const maxLength = Math.max(question.num1.length, question.num2.length, question.correctAnswer.length);
      const padNumber = (num, length) => num.padStart(length, ' ');
      
      content += `
        <div class="question">
          <div class="question-header">
            <h3>Question ${question.id}</h3>
          </div>
          
          <!-- Question Content - Side by Side Layout -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: start;">
            <!-- Math Problem Section -->
            <div class="math-problem">
              <div class="problem-grid" style="grid-template-columns: 40px repeat(${maxLength}, 40px) auto;">
                <!-- Carry-over row -->
                <div></div>
      `;
      
      // Carry inputs
      for (let i = 0; i < maxLength; i++) {
        if (i < maxLength - 1) {
          content += `<input type="text" class="carry-input" placeholder="" />`;
        } else {
          content += `<div></div>`;
        }
      }
      content += `<div class="carry-label">Carry</div>`;
      
      // First number row
      content += `<div></div>`;
      const paddedNum1 = padNumber(question.num1, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum1[i] === ' ' ? '' : paddedNum1[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Second number row with operator
      content += `<div class="operator">+</div>`;
      const paddedNum2 = padNumber(question.num2, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum2[i] === ' ' ? '' : paddedNum2[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Line row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<div class="answer-line"></div>`;
      }
      content += `<div></div>`;
      
      // Answer row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<input type="text" class="answer-input" maxlength="1" />`;
      }
      content += `<div></div>`;
      
      content += `
              </div>
            </div>

            <!-- Step-by-Step Solution Section -->
            <div class="solution-section">
              <div style="margin-bottom: 10px;">
                <h4>Step-by-Step Solution:</h4>
              </div>
              <div class="explanation-steps" style="font-size: 14px;">
      `;
      
      // Add solution steps
      console.log(`Question ${question.id} explanation:`, question.explanation);
      if (question.explanation && question.explanation.steps) {
        console.log(`Question ${question.id} has ${question.explanation.steps.length} steps`);
        question.explanation.steps.forEach((step, stepIndex) => {
          console.log(`Step ${stepIndex + 1}:`, step);
          content += `
                <div style="margin-bottom: 8px; padding: 6px; background-color: white; border-radius: 4px; border: 1px solid #e9ecef; font-size: 13px;">
                  <strong style="color: #667eea;">${step.placeValue}:</strong>
                  <span style="font-family: monospace; margin-left: 8px;">
                    ${step.digitA} + ${step.digitB}
                    ${step.carry > 0 ? ` + ${step.carry}` : ''} = ${step.sum}
                    ${step.sum >= 10 ? ` → ${step.resultDigit} (carry ${step.newCarry})` : ` → ${step.resultDigit}`}
                  </span>
                </div>
          `;
        });
      } else {
        console.log(`Question ${question.id} has no explanation or steps`);
        // Add a fallback message if no explanation is available
        content += `
                <div style="margin-bottom: 8px; padding: 6px; background-color: #fff3cd; border-radius: 4px; border: 1px solid #ffeaa7; font-size: 13px; color: #856404;">
                  <strong>Note:</strong> Step-by-step solution not available for this question.
                </div>
        `;
      }
      
      content += `
                <div style="margin-top: 8px; padding: 6px; background-color: #d4edda; border-radius: 4px; border: 1px solid #c3e6cb; text-align: center; font-weight: bold; color: #155724; font-size: 13px;">
                  <strong>Answer: ${question.correctAnswer}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    });

    return content;
  };

  // Generate print content for questions only (without solutions)
  const generateQuestionsOnlyContent = () => {
    let content = `
      <div class="header">
        <h1>BellMaths - Addition Exam</h1>
        <p>Grade Level: ${examConfig.gradeLevel} | Total Questions: ${totalQuestions}</p>
        <p>Date: ${new Date().toLocaleDateString()}</p>
        <p>Student Name: _________________</p>
        <p style="color: #28a745; font-style: italic;">🌱 Eco-Friendly: This digital exam saves paper and helps protect our environment!</p>
      </div>
    `;

    examQuestions.forEach((question, index) => {
      if (index > 0 && index % 5 === 0) {
        content += '<div class="page-break"></div>';
      }
      
      const maxLength = Math.max(question.num1.length, question.num2.length, question.correctAnswer.length);
      const padNumber = (num, length) => num.padStart(length, ' ');
      
      content += `
        <div class="question">
          <div class="question-header">
            <h3>Question ${question.id}</h3>
          </div>
          
          <!-- Math Problem Section -->
          <div class="math-problem">
            <div class="problem-grid" style="grid-template-columns: 40px repeat(${maxLength}, 40px) auto;">
              <!-- Carry-over row -->
              <div></div>
      `;
      
      // Carry inputs
      for (let i = 0; i < maxLength; i++) {
        if (i < maxLength - 1) {
          content += `<input type="text" class="carry-input" placeholder="" />`;
        } else {
          content += `<div></div>`;
        }
      }
      content += `<div class="carry-label">Carry</div>`;
      
      // First number row
      content += `<div></div>`;
      const paddedNum1 = padNumber(question.num1, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum1[i] === ' ' ? '' : paddedNum1[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Second number row with operator
      content += `<div class="operator">+</div>`;
      const paddedNum2 = padNumber(question.num2, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum2[i] === ' ' ? '' : paddedNum2[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Line row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<div class="answer-line"></div>`;
      }
      content += `<div></div>`;
      
      // Answer row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<input type="text" class="answer-input" maxlength="1" />`;
      }
      content += `<div></div>`;
      
      content += `
            </div>
          </div>
        </div>
      `;
    });

    return content;
  };

  // Print questions only (without solutions)
  const printQuestionsOnly = () => {
    // Track print questions
    metricsTracker.trackPrintQuestions(examConfig.operation, examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    const printContent = generateQuestionsOnlyContent();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>BellMaths Exam - Questions Only</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header h1 {
              color: #667eea;
              margin: 0 0 10px 0;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .question { 
              margin-bottom: 40px; 
              page-break-inside: avoid; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .question-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }
            .question-header h3 {
              margin: 0;
              color: #667eea;
            }
            .math-problem {
              margin: 20px 0;
            }
            .problem-grid {
              display: grid;
              gap: 0px;
              align-items: center;
              margin-bottom: 20px;
            }
            .digit-input {
              width: 40px;
              height: 50px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f4fd;
            }
            .carry-input {
              width: 30px;
              height: 25px;
              font-size: 0.8rem;
              text-align: center;
              border: none;
              border-bottom: 2px solid #e67e22;
              border-radius: 0;
              background-color: #fff3e0;
              color: #e67e22;
              font-weight: bold;
            }
            .answer-input {
              width: 20px;
              height: 30px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: none;
              border-radius: 0;
              background-color: transparent;
              outline: none;
              position: relative;
            }
            .operator {
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              color: #333;
            }
            .carry-label {
              font-size: 0.8rem;
              color: #e67e22;
              font-weight: bold;
            }
            .answer-line {
              height: 2px;
              background-color: #333;
              margin: 10px 0;
            }
            @media print {
              body { background-color: white; }
              .question { box-shadow: none; border: 1px solid #ddd; }
              .header { box-shadow: none; border: 1px solid #ddd; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  // Print plain questions (simple format without BellMaths UI design)
  const printPlainQuestions = () => {
    // Track print answers
    metricsTracker.trackPrintAnswers(examConfig.operation, examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    
    let plainContent = `
      <html>
        <head>
          <title>Math Exam - Questions</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .header h1 {
              margin: 0 0 10px 0;
              color: #333;
            }
            .question { 
              margin-bottom: 30px; 
              page-break-inside: avoid;
            }
            .question-number {
              font-weight: bold;
              margin-bottom: 10px;
              color: #333;
            }
            .math-problem {
              font-size: 18px;
              margin: 15px 0;
              padding: 15px;
              border: 1px solid #ddd;
              background-color: #f9f9f9;
            }
            .answer-space {
              margin-top: 20px;
              padding: 10px;
              border: 1px solid #ccc;
              background-color: #fff;
              min-height: 40px;
            }
            @media print {
              body { margin: 15px; }
              .question { margin-bottom: 25px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Math Exam</h1>
            <p>Student Name: _________________</p>
            <p>Date: _________________</p>
            <p>Total Questions: ${examQuestions.length}</p>
          </div>
    `;

    examQuestions.forEach((question, index) => {
      plainContent += `
        <div class="question">
          <div class="question-number">Question ${index + 1}</div>
          <div class="math-problem">
            ${question.num1} + ${question.num2} = ?
          </div>
          <div class="answer-space">
            Answer: _________________
          </div>
        </div>
      `;
    });

    plainContent += `
        </body>
      </html>
    `;
    
    printWindow.document.write(plainContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Print individual question with solution
  const printQuestionWithSolution = (question) => {
    const printWindow = window.open('', '_blank');
    const maxLength = Math.max(question.num1.length, question.num2.length, question.correctAnswer.length);
    const padNumber = (num, length) => num.padStart(length, ' ');
    
    const printContent = `
      <html>
        <head>
          <title>BellMaths - Question ${question.id} with Solution</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              background-color: white;
              font-size: 14px;
            }
            .header { 
              text-align: center; 
              margin-bottom: 20px; 
              padding: 15px;
              border-bottom: 2px solid #667eea;
            }
            .header h1 {
              color: #667eea;
              margin: 0 0 10px 0;
              font-size: 18px;
            }
            .question-container {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .question-header {
              margin-bottom: 15px;
              padding-bottom: 8px;
              border-bottom: 1px solid #e9ecef;
            }
            .question-header h3 {
              margin: 0;
              color: #667eea;
              font-size: 16px;
            }
            .math-problem {
              margin: 15px 0;
            }
            .problem-grid {
              display: grid;
              grid-template-columns: 40px repeat(${maxLength}, 40px) auto;
              gap: 0px;
              align-items: center;
              margin-bottom: 15px;
            }
            .digit-input {
              width: 40px;
              height: 50px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f4fd;
            }
            .carry-input {
              width: 30px;
              height: 25px;
              font-size: 0.8rem;
              text-align: center;
              border: none;
              border-bottom: 2px solid #e67e22;
              border-radius: 0;
              background-color: #fff3e0;
              color: #e67e22;
              font-weight: bold;
            }
            .operator {
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              color: #333;
            }
            .answer-line {
              height: 2px;
              background-color: #333;
              margin: 10px 0;
            }
            .answer-input {
              width: 20px;
              height: 30px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: none;
              border-radius: 0;
              background-color: transparent;
            }
            .solution-section {
              margin-top: 20px;
              padding: 15px;
              background-color: #f8f9fa;
              border-radius: 6px;
              border: 1px solid #e9ecef;
            }
            .solution-section h4 {
              margin: 0 0 10px 0;
              color: #667eea;
              font-size: 14px;
            }
            .explanation-steps {
              font-size: 12px;
            }
            .step-item {
              margin-bottom: 6px;
              padding: 4px;
              background-color: white;
              border-radius: 3px;
              border: 1px solid #e9ecef;
            }
            .step-label {
              color: #667eea;
              font-weight: bold;
            }
            .step-calculation {
              font-family: monospace;
              margin-left: 8px;
            }
            .final-answer {
              margin-top: 8px;
              padding: 6px;
              background-color: #d4edda;
              border-radius: 4px;
              border: 1px solid #c3e6cb;
              text-align: center;
              font-weight: bold;
              color: #155724;
              font-size: 12px;
            }
            @media print {
              body { margin: 10px; }
              .question-container { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>BellMaths - Question ${question.id}</h1>
            <p>Addition Problem with Step-by-Step Solution</p>
            <p>Date: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="question-container">
            <div class="question-header">
              <h3>Question ${question.id}</h3>
            </div>
            
            <div class="math-problem">
              <div class="problem-grid">
                <!-- Carry-over row -->
                <div></div>
    `;
    
    // Carry inputs
    for (let i = 0; i < maxLength; i++) {
      if (i < maxLength - 1) {
        printContent += `<input type="text" class="carry-input" placeholder="" />`;
      } else {
        printContent += `<div></div>`;
      }
    }
    printContent += `<div style="font-size: 0.8rem; color: #e67e22; font-weight: bold;">Carry</div>`;
    
    // First number row
    printContent += `<div></div>`;
    const paddedNum1 = padNumber(question.num1, maxLength);
    for (let i = 0; i < maxLength; i++) {
      const digit = paddedNum1[i] === ' ' ? '' : paddedNum1[i];
      printContent += `<input type="text" class="digit-input" value="${digit}" readonly />`;
    }
    printContent += `<div></div>`;
    
    // Second number row with operator
    printContent += `<div class="operator">+</div>`;
    const paddedNum2 = padNumber(question.num2, maxLength);
    for (let i = 0; i < maxLength; i++) {
      const digit = paddedNum2[i] === ' ' ? '' : paddedNum2[i];
      printContent += `<input type="text" class="digit-input" value="${digit}" readonly />`;
    }
    printContent += `<div></div>`;
    
    // Line row
    printContent += `<div></div>`;
    for (let i = 0; i < maxLength; i++) {
      printContent += `<div class="answer-line"></div>`;
    }
    printContent += `<div></div>`;
    
    // Answer row
    printContent += `<div></div>`;
    for (let i = 0; i < maxLength; i++) {
      printContent += `<input type="text" class="answer-input" maxlength="1" />`;
    }
    printContent += `<div></div>`;
    
    printContent += `
              </div>
            </div>
            
            <div class="solution-section">
              <h4>Step-by-Step Solution:</h4>
              <div class="explanation-steps">
    `;
    
    // Add solution steps
    if (question.explanation && question.explanation.steps) {
      question.explanation.steps.forEach((step, index) => {
        printContent += `
                  <div class="step-item">
                    <span class="step-label">${step.placeValue}:</span>
                    <span class="step-calculation">
                      ${step.digitA} + ${step.digitB}
                      ${step.carry > 0 ? ` + ${step.carry}` : ''} = ${step.sum}
                      ${step.sum >= 10 ? ` → ${step.resultDigit} (carry ${step.newCarry})` : ` → ${step.resultDigit}`}
                    </span>
                  </div>
        `;
      });
    }
    
    printContent += `
                <div class="final-answer">
                  <strong>Answer: ${question.correctAnswer}</strong>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Download exam with multiple formats
  const downloadExam = () => {
    const examData = {
      questions: examQuestions,
      config: examConfig,
      timestamp: new Date().toISOString(),
      completed: examCompleted
    };
    
    // Create download options (JSON format removed as requested)
    const downloadOptions = [
      {
        name: 'Filled Answer Sheet (HTML)',
        data: generateAnswerSheetHTML(),
        type: 'text/html',
        filename: `bellMaths_answersheet_${new Date().toISOString().split('T')[0]}.html`
      },
      {
        name: 'CSV Report',
        data: generateCSVReport(),
        type: 'text/csv',
        filename: `bellMaths_report_${new Date().toISOString().split('T')[0]}.csv`
      }
    ];

    // Create download menu
    const downloadMenu = document.createElement('div');
    downloadMenu.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border: 2px solid #667eea;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 1000;
      min-width: 300px;
    `;
    
    downloadMenu.innerHTML = `
      <h3 style="margin: 0 0 15px 0; color: #667eea;">Download Options</h3>
      ${downloadOptions.map(option => `
        <button style="
          display: block;
          width: 100%;
          margin: 8px 0;
          padding: 12px;
          background: #667eea;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        " onclick="downloadFile('${option.name}', '${option.type}', '${option.filename}')">
          ${option.name}
        </button>
      `).join('')}
      <button style="
        display: block;
        width: 100%;
        margin: 8px 0;
        padding: 12px;
        background: #6c757d;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
      " onclick="closeDownloadMenu()">
        Cancel
      </button>
    `;

    // Add download function to window
    window.downloadFile = (name, type, filename) => {
      const option = downloadOptions.find(opt => opt.name === name);
      if (option) {
        const dataStr = option.data;
        const dataBlob = new Blob([dataStr], { type: option.type });
        const url = URL.createObjectURL(dataBlob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = option.filename;
        link.click();
        
        URL.revokeObjectURL(url);
      }
      closeDownloadMenu();
    };

    window.closeDownloadMenu = () => {
      document.body.removeChild(downloadMenu);
      delete window.downloadFile;
      delete window.closeDownloadMenu;
    };

    document.body.appendChild(downloadMenu);
  };

  // Generate filled answer sheet HTML
  const generateAnswerSheetHTML = () => {
    const results = calculateResults();
    
    let content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>BellMaths - Filled Answer Sheet</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header h1 {
              color: #667eea;
              margin: 0 0 10px 0;
            }
            .summary {
              margin: 30px 0;
              padding: 20px;
              background-color: #f8f9fa;
              border-radius: 8px;
              border: 1px solid #e9ecef;
            }
            .summary h3 {
              margin: 0 0 15px 0;
              color: #667eea;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
              margin-bottom: 20px;
            }
            .summary-item {
              padding: 10px;
              background-color: white;
              border-radius: 6px;
              border: 1px solid #e9ecef;
            }
            .summary-label {
              font-weight: bold;
              color: #667eea;
            }
            .summary-value {
              margin-left: 10px;
              font-weight: bold;
            }
            .question { 
              margin-bottom: 40px; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .question-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }
            .question-header h3 {
              margin: 0;
              color: #667eea;
            }
            .result-badge {
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
              font-weight: bold;
            }
            .result-badge.correct {
              background-color: #d4edda;
              color: #155724;
            }
            .result-badge.incorrect {
              background-color: #f8d7da;
              color: #721c24;
            }
            .math-problem {
              margin: 20px 0;
            }
            .problem-grid {
              display: grid;
              gap: 0px;
              align-items: center;
              margin-bottom: 20px;
            }
            .digit-input {
              width: 40px;
              height: 50px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f4fd;
            }
            .carry-input {
              width: 30px;
              height: 25px;
              font-size: 0.8rem;
              text-align: center;
              border: none;
              border-bottom: 2px solid #e67e22;
              border-radius: 0;
              background-color: #fff3e0;
              color: #e67e22;
              font-weight: bold;
            }
            .answer-input {
              width: 20px;
              height: 30px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: none;
              border-radius: 0;
              background-color: transparent;
              outline: none;
              position: relative;
            }
            .answer-input.correct {
              color: #28a745;
              font-weight: bold;
            }
            .answer-input.incorrect {
              color: #dc3545;
              font-weight: bold;
            }
            .operator {
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              color: #333;
            }
            .carry-label {
              font-size: 0.8rem;
              color: #e67e22;
              font-weight: bold;
            }
            .answer-line {
              height: 2px;
              background-color: #333;
              margin: 10px 0;
            }
            .solution-section {
              margin-top: 20px;
            }
            .explanation-steps {
              font-size: 14px;
            }
            .explanation-steps > div {
              margin-bottom: 8px;
              padding: 6px;
              background-color: white;
              border-radius: 4px;
              border: 1px solid #e9ecef;
              font-size: 13px;
            }
            .question > div[style*="grid-template-columns"] {
              display: grid !important;
              grid-template-columns: 1fr 1fr !important;
              gap: 20px !important;
              align-items: start !important;
            }
            @media print {
              body { 
                background-color: white; 
                margin: 15px;
              }
              .question { 
                page-break-inside: avoid; 
                box-shadow: none; 
                border: 1px solid #ddd; 
                margin-bottom: 30px;
              }
              .header { 
                box-shadow: none; 
                border: 1px solid #ddd; 
                margin-bottom: 20px;
              }
              .summary { 
                page-break-inside: avoid; 
                box-shadow: none; 
                border: 1px solid #ddd; 
                margin: 20px 0;
              }
            }
          </style>
        </head>
        <body>
          <!-- Header -->
          <div class="header">
            <h1>BellMaths - Filled Answer Sheet</h1>
            <p>Grade Level: ${examConfig.gradeLevel} | Total Questions: ${totalQuestions}</p>
            <p>Date: ${new Date().toLocaleDateString()}</p>
            <p>Student Name: _________________</p>
            <p style="color: #28a745; font-style: italic;">🌱 Eco-Friendly: This digital exam saves paper and helps protect our environment!</p>
          </div>
          
          <!-- Exam Results Summary -->
          <div class="summary">
            <h3>Exam Results Summary</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <span class="summary-label">Correct Answers:</span>
                <span class="summary-value">${results.correct}/${results.total}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Percentage:</span>
                <span class="summary-value">${results.percentage}%</span>
              </div>
            </div>
          </div>
    `;

    examQuestions.forEach((question, index) => {
      const maxLength = Math.max(question.num1.length, question.num2.length, question.correctAnswer.length);
      
      content += `
        <div class="question">
          <div class="question-header">
            <h3>Question ${question.id}</h3>
            ${examCompleted ? `
              <span class="result-badge ${question.isCorrect ? 'correct' : 'incorrect'}">
                ${question.isCorrect ? '✓ Correct' : '✗ Incorrect'}
              </span>
            ` : ''}
          </div>
          
          <!-- Question Content - Side by Side Layout -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: start;">
            <!-- Math Problem Section -->
            <div class="math-problem">
              <div class="problem-grid" style="grid-template-columns: 40px repeat(${maxLength}, 40px) auto;">
                <!-- Carry-over row -->
                <div></div>
      `;
      
      // Carry inputs
      for (let i = 0; i < maxLength; i++) {
        if (i < maxLength - 1) {
          const carryValue = question.carryOver && question.carryOver[i] ? question.carryOver[i] : '';
          content += `<input type="text" class="carry-input" value="${carryValue}" readonly />`;
        } else {
          content += `<div></div>`;
        }
      }
      content += `<div class="carry-label">Carry</div>`;
      
      // First number row
      content += `<div></div>`;
      const paddedNum1 = question.num1.padStart(maxLength, ' ');
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum1[i] === ' ' ? '' : paddedNum1[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Second number row with operator
      content += `<div class="operator">+</div>`;
      const paddedNum2 = question.num2.padStart(maxLength, ' ');
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum2[i] === ' ' ? '' : paddedNum2[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Line row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<div class="answer-line"></div>`;
      }
      content += `<div></div>`;
      
      // Answer row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        const answerDigit = question.userAnswer && question.userAnswer[i] ? question.userAnswer[i] : '';
        const isCorrect = examCompleted ? (answerDigit === question.correctAnswer[i]) : null;
        const answerClass = examCompleted ? (isCorrect ? 'correct' : 'incorrect') : '';
        content += `<input type="text" class="answer-input ${answerClass}" value="${answerDigit}" readonly />`;
      }
      content += `<div></div>`;
      
      content += `
              </div>
            </div>

            <!-- Step-by-Step Solution Section -->
            <div class="solution-section">
              <div style="margin-bottom: 10px;">
                <h4>Step-by-Step Solution:</h4>
              </div>
              <div class="explanation-steps" style="font-size: 14px;">
      `;
      
      // Add solution steps
      if (question.explanation && question.explanation.steps) {
        question.explanation.steps.forEach((step, stepIndex) => {
          content += `
                <div style="margin-bottom: 8px; padding: 6px; background-color: white; border-radius: 4px; border: 1px solid #e9ecef; font-size: 13px;">
                  <strong style="color: #667eea;">${step.placeValue}:</strong>
                  <span style="font-family: monospace; margin-left: 8px;">
                    ${step.digitA} + ${step.digitB}
                    ${step.carry > 0 ? ` + ${step.carry}` : ''} = ${step.sum}
                    ${step.sum >= 10 ? ` → ${step.resultDigit} (carry ${step.newCarry})` : ` → ${step.resultDigit}`}
                  </span>
                </div>
          `;
        });
      } else {
        // Add a fallback message if no explanation is available
        content += `
                <div style="margin-bottom: 8px; padding: 6px; background-color: #fff3cd; border-radius: 4px; border: 1px solid #ffeaa7; font-size: 13px; color: #856404;">
                  <strong>Note:</strong> Step-by-step solution not available for this question.
                </div>
        `;
      }
      
      content += `
                <div style="margin-top: 8px; padding: 6px; background-color: #d4edda; border-radius: 4px; border: 1px solid #c3e6cb; text-align: center; font-weight: bold; color: #155724; font-size: 13px;">
                  <strong>Answer: ${question.correctAnswer}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    });

    content += `
    </body>
    </html>
    `;

    return content;
  };

  // Generate CSV report
  const generateCSVReport = () => {
    const results = calculateResults();
    let csv = 'Question,Number1,Number2,CorrectAnswer,UserAnswer,CarryOver,IsCorrect\n';
    
    examQuestions.forEach(question => {
      csv += `${question.id},"${question.num1}","${question.num2}","${question.correctAnswer}","${(question.userAnswer || []).join('')}","${(question.carryOver || []).join('')}","${question.isCorrect ? 'Yes' : 'No'}"\n`;
    });
    
    csv += `\nSummary\n`;
    csv += `Total Questions,${results.total}\n`;
    csv += `Correct Answers,${results.correct}\n`;
    csv += `Percentage,${results.percentage}%\n`;
    csv += `Grade,${results.percentage >= 90 ? 'A' : results.percentage >= 80 ? 'B' : results.percentage >= 70 ? 'C' : results.percentage >= 60 ? 'D' : 'F'}\n`;
    
    return csv;
  };

  // Calculate results
  const calculateResults = () => {
    const correct = examQuestions.filter(q => q.isCorrect).length;
    const total = examQuestions.length;
    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
    
    return { correct, total, percentage };
  };

  // Handle input focus - show keyboard and position it
  const handleInputFocus = (index, type, questionIndex) => {
    console.log('Exam handleInputFocus:', { index, type, questionIndex });
    setActiveInput({ index, type, questionIndex });
    setKeyboardVisible(true);
    
    // Position keyboard just next to the input
    const inputElement = document.querySelector(`input[data-question="${questionIndex}"][data-index="${type}-${index}"][data-type="${type}"]`);
    console.log('Found input element:', inputElement);
    
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      const mathProblemRect = mathProblemRefs.current[questionIndex]?.getBoundingClientRect();
      
      console.log('Positioning debug:', {
        inputRect: rect,
        mathProblemRect: mathProblemRect,
        mathProblemRefs: mathProblemRefs.current
      });
      
      if (mathProblemRect) {
        // Calculate position based on place value - move left for higher place values
        const question = examQuestions.find(q => q.id === questionIndex);
        const maxLength = Math.max(question.num1.length, question.num2.length);
        const placeValue = maxLength - index - 1; // 0 = ones, 1 = tens, 2 = hundreds, etc.
        
        // Base position: just next to input (5px offset)
        let x = rect.right - mathProblemRect.left + 5;
        
        // Move left for higher place values to avoid hiding questions
        if (placeValue >= 1) { // tens or higher
          x -= (placeValue * 15); // Move left by 15px per place value
        }
        
        // Ensure keyboard doesn't go too far left
        x = Math.max(x, 50);
        
        // Move keyboard down to avoid hiding the second number
        let y = rect.top - mathProblemRect.top;
        
        // If this is a carry field, move keyboard down more to avoid hiding first number
        if (type === 'carry') {
          y += 60; // Move down by 60px for carry fields
        } else {
          y += 40; // Move down by 40px for answer fields to avoid hiding second number
        }
        
        console.log('Setting keyboard position:', { x, y });
        setKeyboardPosition({ x, y });
      } else {
        console.log('mathProblemRect is null!');
      }
    }
  };

  // Handle input blur - don't hide keyboard, just reposition
  const handleInputBlur = () => {
    // Don't hide keyboard, don't clear active input to allow virtual keyboard input
    // setActiveInput(null); // Commented out to allow virtual keyboard input
  };

  // Handle virtual keyboard input
  const handleVirtualKeyPress = (key) => {
    console.log('Virtual key press:', key, 'Active input:', activeInput);
    if (activeInput) {
      if (activeInput.type === 'answer') {
        console.log('Updating answer at index:', activeInput.index, 'with value:', key);
        handleAnswerChange(activeInput.questionIndex, activeInput.index, key);
        // Removed automatic cursor movement - let students decide where to place cursor
      } else if (activeInput.type === 'carry') {
        console.log('Updating carry at index:', activeInput.index, 'with value:', key);
        handleCarryChange(activeInput.questionIndex, activeInput.index, key);
      }
      
      // Re-focus the active input field after virtual keyboard input
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="${activeInput.type}-${activeInput.index}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle virtual keyboard backspace
  const handleVirtualBackspace = () => {
    console.log('Virtual backspace, Active input:', activeInput);
    if (activeInput) {
      if (activeInput.type === 'answer') {
        console.log('Clearing answer at index:', activeInput.index);
        handleAnswerChange(activeInput.questionIndex, activeInput.index, '');
        // Removed automatic cursor movement - let students decide where to place cursor
      } else if (activeInput.type === 'carry') {
        console.log('Clearing carry at index:', activeInput.index);
        handleCarryChange(activeInput.questionIndex, activeInput.index, '');
      }
      
      // Re-focus the active input field after virtual keyboard backspace
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="${activeInput.type}-${activeInput.index}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle virtual keyboard clear
  const handleVirtualClear = () => {
    console.log('Virtual clear, Active input:', activeInput);
    if (activeInput) {
      if (activeInput.type === 'answer') {
        console.log('Clearing answer at index:', activeInput.index);
        handleAnswerChange(activeInput.questionIndex, activeInput.index, '');
      } else if (activeInput.type === 'carry') {
        console.log('Clearing carry at index:', activeInput.index);
        handleCarryChange(activeInput.questionIndex, activeInput.index, '');
      }
      
      // Re-focus the active input field after virtual keyboard clear
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="${activeInput.type}-${activeInput.index}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, currentIndex, type, questionId) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const inputs = document.querySelectorAll(`input[data-question="${questionId}"][data-index]`);
      const currentInputIndex = Array.from(inputs).findIndex(input => input === e.target);
      
      if (e.shiftKey) {
        // Shift+Tab - move to previous input
        if (currentInputIndex > 0) {
          inputs[currentInputIndex - 1].focus();
        }
      } else {
        // Tab - move to next input
        if (currentInputIndex < inputs.length - 1) {
          inputs[currentInputIndex + 1].focus();
        }
      }
    }
    // Removed automatic cursor movement on backspace and digit entry
    // Let students decide where to place cursor
  };

  // Hide keyboard when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.virtual-keyboard') && 
          !event.target.closest('.digit-input') && 
          !event.target.closest('.carry-input')) {
        setKeyboardVisible(false);
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load saved exam on component mount
  useEffect(() => {
    loadExamFromStorage();
  }, []);

  // Auto-save progress every 30 seconds
  useEffect(() => {
    if (examStarted && examQuestions.length > 0) {
      const interval = setInterval(() => {
        saveExamToStorage(examQuestions);
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [examQuestions, examStarted]);

  // Timer countdown effect
  useEffect(() => {
    let interval = null;
    
    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(time => {
          if (time <= 1) {
            // Time's up - auto submit
            submitExam();
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    } else if (timeRemaining === 0 && timerActive) {
      setTimerActive(false);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerActive]);

  const results = calculateResults();
  const totalPages = Math.ceil(totalQuestions / questionsPerPage);
  const startIndex = (currentPage - 1) * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = examQuestions.slice(startIndex, endIndex);

  if (!examStarted) {
    return (
      <div className="exam-setup">
        <div className="page-title">
          <h2>Exam Setup</h2>
          <p>Configure your addition exam settings</p>
        </div>

        <div className="exam-config">
          <div className="config-item">
            <label>Number of Questions:</label>
            <select 
              value={examConfig.questionCount} 
              onChange={(e) => setExamConfig(prev => ({ ...prev, questionCount: parseInt(e.target.value) }))}
            >
              {[1, 2, 5, 10, 15, 20].map(count => (
                <option 
                  key={count} 
                  value={count}
                  disabled={count > 2}
                  style={count > 2 ? { color: '#999', fontStyle: 'italic' } : {}}
                >
                  {count} {count === 1 ? 'Question' : 'Questions'}
                  {count > 2 && ' (Upgrade to Pro Edition)'}
                </option>
              ))}
            </select>
            {examConfig.questionCount > 2 && (
              <div style={{ 
                marginTop: '8px', 
                padding: '8px 12px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7', 
                borderRadius: '4px',
                fontSize: '14px',
                color: '#856404'
              }}>
                ⚠️ Upgrade to Pro Edition to support {examConfig.questionCount} questions
              </div>
            )}
          </div>

          <div className="config-item">
            <label>Level:</label>
            <select 
              value={examConfig.gradeLevel} 
              onChange={(e) => setExamConfig(prev => ({ ...prev, gradeLevel: e.target.value }))}
            >
              {Array.from({ length: 8 }, (_, i) => i + 1).map(level => (
                <option 
                  key={level} 
                  value={level.toString()}
                  disabled={level > 3}
                  style={level > 3 ? { color: '#999', fontStyle: 'italic' } : {}}
                >
                  Level {level} (Upto {level + 1} digits)
                  {level > 3 && ' (Upgrade to Pro Edition)'}
                </option>
              ))}
            </select>
            {parseInt(examConfig.gradeLevel) > 3 && (
              <div style={{ 
                marginTop: '8px', 
                padding: '8px 12px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7', 
                borderRadius: '4px',
                fontSize: '14px',
                color: '#856404'
              }}>
                ⚠️ Upgrade to Pro Edition to support Level {examConfig.gradeLevel} (Upto {parseInt(examConfig.gradeLevel) + 1} digits)
              </div>
            )}
          </div>

          {/* Timer Configuration */}
          <div className="config-item" style={{ marginTop: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={examConfig.useTimer}
                onChange={(e) => setExamConfig(prev => ({ ...prev, useTimer: e.target.checked }))}
              />
              Enable Timer
            </label>
          </div>

          {examConfig.useTimer && (
            <div className="config-item">
              <label>Time Limit (minutes):</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                <input
                  type="range"
                  min="1"
                  max="180"
                  value={examConfig.timeLimit}
                  onChange={(e) => setExamConfig(prev => ({ ...prev, timeLimit: parseInt(e.target.value) }))}
                  style={{ flex: 1 }}
                />
                <input
                  type="number"
                  min="1"
                  max="180"
                  value={examConfig.timeLimit}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 1 && value <= 180) {
                      setExamConfig(prev => ({ ...prev, timeLimit: value }));
                    }
                  }}
                  style={{ 
                    width: '80px', 
                    padding: '5px', 
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
                <span style={{ fontSize: '14px', color: '#666' }}>
                  {examConfig.timeLimit} min
                </span>
              </div>
            </div>
          )}

          {/* Color Coding Guide */}
          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px', 
            border: '1px solid #e9ecef' 
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#667eea' }}>Color Coding Guide</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ 
                  display: 'inline-block', 
                  width: '20px', 
                  height: '20px', 
                  backgroundColor: '#e8f4fd', 
                  border: '1px solid #ddd',
                  borderRadius: '3px'
                }}></span>
                <span>First & Second Numbers</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ 
                  display: 'inline-block', 
                  width: '20px', 
                  height: '20px', 
                  backgroundColor: '#fff3e0', 
                  border: '1px solid #e67e22',
                  borderRadius: '3px'
                }}></span>
                <span>Carry Values</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ 
                  display: 'inline-block', 
                  width: '20px', 
                  height: '20px', 
                  backgroundColor: '#d4edda', 
                  border: '1px solid #28a745',
                  borderRadius: '3px'
                }}></span>
                <span>Answer Digits (Correct)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ 
                  display: 'inline-block', 
                  width: '20px', 
                  height: '20px', 
                  backgroundColor: '#f8d7da', 
                  border: '1px solid #dc3545',
                  borderRadius: '3px'
                }}></span>
                <span>Answer Digits (Incorrect)</span>
              </div>
            </div>
          </div>

          <button className="btn btn-primary btn-large" onClick={generateExam}>
            Start Exam
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Exam Header */}
      <div className="exam-header">
        <div className="exam-info">
          <h2>Addition Exam - Grade {examConfig.gradeLevel}</h2>
          <p>Question {startIndex + 1} - {Math.min(endIndex, totalQuestions)} of {totalQuestions}</p>
          {examConfig.useTimer && (
            <div style={{ 
              marginTop: '10px',
              padding: '10px 15px',
              backgroundColor: timeRemaining <= 300 ? '#f8d7da' : '#d4edda', // Red if < 5 min, green otherwise
              borderRadius: '6px',
              border: `2px solid ${timeRemaining <= 300 ? '#dc3545' : '#28a745'}`,
              textAlign: 'center'
            }}>
              <strong style={{ 
                color: timeRemaining <= 300 ? '#721c24' : '#155724',
                fontSize: '18px'
              }}>
                Time Remaining: {formatTime(timeRemaining)}
              </strong>
            </div>
          )}
        </div>
        
        <div className="exam-actions">
          {!examCompleted ? (
            <>
              {/* Before exam submission - show both print buttons */}
              <button className="btn btn-secondary" onClick={printQuestionsOnly}>
                Print BellMaths Notebook
              </button>
              <button className="btn btn-secondary" onClick={printPlainQuestions}>
                Print Questions
              </button>
            </>
          ) : (
            <>
              {/* After exam submission - show both print buttons */}
              <button className="btn btn-secondary" onClick={() => {
                const content = generateAnswerSheetHTML();
                const printWindow = window.open('', '_blank');
                printWindow.document.write(content);
                printWindow.document.close();
                printWindow.print();
              }}>
                Print BellMaths Notebook with Answers
              </button>
              <button className="btn btn-secondary" onClick={printPlainQuestions}>
                Print Questions
              </button>
            </>
          )}
          {/* Restart Exam Button - Moved to top as requested */}
          {showResults && (
            <button 
              className="btn btn-primary" 
              onClick={() => {
                setExamStarted(false);
                setExamCompleted(false);
                setShowResults(false);
                setExamQuestions([]);
                setCurrentPage(1);
                setKeyboardVisible(false);
                setActiveInput(null);
                setTimeRemaining(0);
                setTimerActive(false);
              }}
              style={{ 
                padding: '10px 20px', 
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              Restart Exam
            </button>
          )}
          {/* Download Progress button hidden as requested */}
          {!examCompleted && (
            <button className="btn btn-success" onClick={submitExam}>
              Submit Exam
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="exam-progress">
        <div className="progress-bar">
                  <div 
          className="progress-fill" 
          style={{ width: `${(examQuestions.filter(q => q.userAnswer && q.userAnswer.some(digit => digit)).length / totalQuestions) * 100}%` }}
        ></div>
        </div>
        <span className="progress-text">
          {examQuestions.filter(q => q.userAnswer && q.userAnswer.some(digit => digit)).length} / {totalQuestions} answered
        </span>
      </div>

      {/* Question Navigation Bar */}
      {!showResults && examQuestions.length > 0 && (
        <div style={{
          position: 'sticky',
          top: '0',
          zIndex: 100,
          backgroundColor: 'white',
          padding: '15px',
          borderBottom: '2px solid #e9ecef',
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <h4 style={{ margin: 0, color: '#667eea' }}>Quick Navigation</h4>
            <span style={{ fontSize: '14px', color: '#666' }}>
              Click any question number to jump to it
            </span>
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            maxHeight: '120px',
            overflowY: 'auto'
          }}>
            {examQuestions.map((question, index) => {
              const hasAnswer = question.userAnswer && question.userAnswer.some(digit => digit);
              return (
                <button
                  key={question.id}
                  onClick={() => {
                    const questionElement = document.getElementById(`question-${question.id}`);
                    if (questionElement) {
                      questionElement.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center' 
                      });
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    border: '2px solid',
                    borderColor: hasAnswer ? '#28a745' : '#e9ecef',
                    backgroundColor: hasAnswer ? '#d4edda' : 'white',
                    color: hasAnswer ? '#155724' : '#666',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    minWidth: '40px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {question.id}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Questions */}
      <div className="questions-container">
        {/* Exam Results Summary - Positioned above question 1 as requested */}
        {showResults && (
          <div className="exam-results" style={{ 
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #667eea'
          }}>
            <h3 style={{ color: '#667eea', marginBottom: '15px' }}>Exam Results Summary</h3>
            <div className="results-summary" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '15px',
              marginBottom: '20px'
            }}>
              <div className="result-item" style={{ 
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <span className="result-label" style={{ fontWeight: 'bold', color: '#667eea' }}>Correct Answers:</span>
                <span className="result-value" style={{ marginLeft: '10px', fontWeight: 'bold' }}>{results.correct}/{results.total}</span>
              </div>
              <div className="result-item" style={{ 
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <span className="result-label" style={{ fontWeight: 'bold', color: '#667eea' }}>Percentage:</span>
                <span className="result-value" style={{ marginLeft: '10px', fontWeight: 'bold' }}>{results.percentage}%</span>
              </div>
            </div>
          </div>
        )}
        
        {currentQuestions.map((question, index) => {
          const maxLength = Math.max(question.num1.length, question.num2.length, question.correctAnswer.length);
          const padNumber = (num, length) => num.padStart(length, ' ');
          
          return (
            <div key={question.id} id={`question-${question.id}`} className="question-container">
              <div className="question-header">
                <h3>Question {question.id}</h3>
                {examCompleted && (
                  <span className={`result-badge ${question.isCorrect ? 'correct' : 'incorrect'}`}>
                    {question.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                )}
              </div>

              {/* Question Content - Side by Side Layout */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: examCompleted && question.explanation ? '1fr 1fr' : '1fr',
                gap: '20px',
                alignItems: 'start'
              }}>
                {/* Math Problem Section */}
                <div className="math-problem" ref={(el) => mathProblemRefs.current[question.id] = el} style={{ 
                  display: 'grid', 
                  gridTemplateColumns: `40px repeat(${maxLength}, 40px) auto`,
                  gap: '0px',
                  alignItems: 'center',
                  position: 'relative'
                }}>
                  {/* Carry-over row */}
                  <div></div>
                  {Array.from({ length: maxLength }, (_, i) => (
                    <div key={`carry-${i}`} style={{ textAlign: 'center' }}>
                      {i < maxLength - 1 && (
                        <input
                          type="text"
                          className="carry-input"
                          value={question.carryOver && question.carryOver[i] ? question.carryOver[i] : ''}
                          onChange={(e) => {
                            handleCarryChange(question.id, i, e.target.value);
                          }}
                          onFocus={(e) => handleInputFocus(i, 'carry', question.id)}
                          onBlur={handleInputBlur}
                          onKeyDown={(e) => handleKeyDown(e, i, 'carry', question.id)}
                          disabled={examCompleted}
                          data-question={question.id}
                          data-index={`carry-${i}`}
                          data-type="carry"
                          style={{
                            width: '30px',
                            height: '25px',
                            fontSize: '0.8rem',
                            textAlign: 'center',
                            border: 'none',
                            borderBottom: '2px solid #e67e22',
                            borderRadius: '0',
                            backgroundColor: '#fff3e0',
                            color: '#e67e22',
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                    </div>
                  ))}
                  <div style={{ fontSize: '0.8rem', color: '#e67e22', fontWeight: 'bold' }}>
                    Carry
                  </div>

                  {/* First number row */}
                  <div></div>
                  {padNumber(question.num1, maxLength).split('').map((digit, i) => (
                    <input
                      key={`num1-${i}`}
                      type="text"
                      className="digit-input"
                      value={digit === ' ' ? '' : digit}
                      readOnly
                      style={{ 
                        backgroundColor: '#e8f4fd',
                        width: '40px',
                        height: '50px',
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        border: '2px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                  ))}
                  <div></div>

                  {/* Second number row with operator */}
                  <span className="operator" style={{ textAlign: 'center' }}>+</span>
                  {padNumber(question.num2, maxLength).split('').map((digit, i) => (
                    <input
                      key={`num2-${i}`}
                      type="text"
                      className="digit-input"
                      value={digit === ' ' ? '' : digit}
                      readOnly
                      style={{ 
                        backgroundColor: '#e8f4fd',
                        width: '40px',
                        height: '50px',
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        border: '2px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                  ))}
                  <div></div>

                  {/* Line row */}
                  <div></div>
                  {Array.from({ length: maxLength }, (_, i) => (
                    <div key={`line-${i}`} style={{ 
                      height: '2px', 
                      backgroundColor: '#333',
                      margin: '10px 0'
                    }}></div>
                  ))}
                  <div></div>

                  {/* Answer row */}
                  <div></div>
                  {Array.from({ length: maxLength }, (_, i) => (
                    <div key={`answer-container-${i}`} style={{ 
                      width: '40px', 
                      height: '50px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      <input
                        key={`answer-${i}`}
                        type="text"
                        className={`digit-input ${examCompleted ? 
                          (question.userAnswer[i] === question.correctAnswer[i] ? 'correct' : 'incorrect') : ''}`}
                        value={question.userAnswer && question.userAnswer[i] ? question.userAnswer[i] : ''}
                        onChange={(e) => {
                          handleAnswerChange(question.id, i, e.target.value);
                        }}
                        onFocus={(e) => handleInputFocus(i, 'answer', question.id)}
                        onBlur={handleInputBlur}
                        onKeyDown={(e) => handleKeyDown(e, i, 'answer', question.id)}
                        disabled={examCompleted}
                        data-question={question.id}
                        data-index={`answer-${i}`}
                        data-type="answer"
                        maxLength={1}
                        style={{
                          width: '20px',
                          height: '30px',
                          textAlign: 'center',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          border: 'none',
                          borderRadius: '0',
                          backgroundColor: 'transparent',
                          transition: 'border-color 0.3s ease',
                          outline: 'none'
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        bottom: '5px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '25px',
                        height: '3px',
                        backgroundColor: '#333',
                        borderRadius: '1px'
                      }}></div>
                    </div>
                  ))}
                  <div></div>
                  
                  {/* Virtual Keyboard - Only show for this question if it's active */}
                  {activeInput && activeInput.questionIndex === question.id && keyboardVisible && (
                    <VirtualKeyboard
                      onKeyPress={handleVirtualKeyPress}
                      onBackspace={handleVirtualBackspace}
                      onClear={handleVirtualClear}
                      position={keyboardPosition}
                      isVisible={keyboardVisible}
                    />
                  )}
                </div>

                {/* Step-by-Step Solution Section (shown after completion) */}
                {examCompleted && question.explanation && (
                  <div className="solution-section">
                    <div style={{ marginBottom: '10px' }}>
                      <h4>Step-by-Step Solution:</h4>
                    </div>
                    <div className="explanation-steps" style={{ fontSize: '14px' }}>
                      {question.explanation.steps.map((step, stepIndex) => (
                        <div key={stepIndex} style={{ 
                          marginBottom: '8px', 
                          padding: '6px', 
                          backgroundColor: 'white', 
                          borderRadius: '4px', 
                          border: '1px solid #e9ecef',
                          fontSize: '13px'
                        }}>
                          <strong style={{ color: '#667eea' }}>{step.placeValue}:</strong>
                          <span style={{ fontFamily: 'monospace', marginLeft: '8px' }}>
                            {step.digitA} + {step.digitB}
                            {step.carry > 0 && ` + ${step.carry}`} = {step.sum}
                            {step.sum >= 10 && ` → ${step.resultDigit} (carry ${step.newCarry})`}
                            {step.sum < 10 && ` → ${step.resultDigit}`}
                          </span>
                        </div>
                      ))}
                      <div style={{ 
                        marginTop: '8px', 
                        padding: '6px', 
                        backgroundColor: '#d4edda', 
                        borderRadius: '4px', 
                        border: '1px solid #c3e6cb',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: '#155724',
                        fontSize: '13px'
                      }}>
                        <strong>Answer: {question.correctAnswer}</strong>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        
      </div>



      {/* Pagination */}
      <div className="exam-pagination">
        <button 
          className="btn btn-secondary" 
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        
        <span className="page-info">
          Page {currentPage} of {totalPages}
        </span>
        
        <button 
          className="btn btn-secondary" 
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      {/* Bottom Submit Button */}
      {!examCompleted && (
        <div style={{ 
          textAlign: 'center', 
          marginTop: '30px', 
          padding: '20px',
          borderTop: '2px solid #e9ecef'
        }}>
          <button className="btn btn-success btn-large" onClick={submitExam}>
            Submit Exam
          </button>
        </div>
      )}


    </>
  );
};

export default Exam; 